import React from 'react';
import PropTypes from 'prop-types';

import {Dialog, DialogActions, DialogContent, DialogTitle} from 'views/components';
import {Grid, Stack, Typography, Button, Link, Box, Skeleton} from 'views/components';
import {Cached} from 'views/icons';
import {Translate, translate, MarkdownViewer, ShortDateText, getEventStatusStyles} from 'views/module';

import backend from 'utils/backend';
import moment from 'moment-timezone';

const ViewEvent = (props) => {
  const [caregiverData, setCaregiverData] = React.useState(null);

  const onClose = props.onClose;
  const eventRn = props.eventRn;
  const event = props.event;
  const openRecurrence = props.openRecurrence;
  const openLiveIn = props.openLiveIn;
  const deleteEvent = props.deleteEvent;

  const startTimeMoment = moment.tz(event.eventData.startTime, event.eventData.timezone);
  const endTimeMoment = moment.tz(event.eventData.endTime, event.eventData.timezone);

  const statusStyles = getEventStatusStyles({event});

  // Load caregiver data
  React.useEffect(() => {
    backend.caregiver.getCaregiver(event.caregiverData.caregiverRn, true)
        .then((e) => {
          setCaregiverData(e);
        })
        .catch(() => {
          console.error('Failed to load caregiver data');
        });
  }, [event]);

  return (
    <Dialog onClose={onClose} open={true}>
      <DialogTitle onClose={onClose}>
        <Translate expression="client.ClientDetail.Scheduling.ViewEvent" />
      </DialogTitle>
      <DialogContent>
        <Stack spacing={1} >
          {
            statusStyles.isInactive &&
            <Box
              sx={{
                background: statusStyles.background,
                border: `1px solid ${statusStyles.borderColor}`,
                borderRadius: 2,
                color: statusStyles.textColor,
                fontSize: '14px',
                fontWeight: 600,
                px: 1.5,
                py: 0.5,
                width: 'fit-content',
              }}
            >
              {statusStyles.label}
            </Box>
          }
          <Stack>
            <Typography
              sx={{
                fontWeight: 600,
              }}
            >
              <Translate expression='client.ClientDetail.Scheduling.ScheduleDetail.EventInfo.Title'/>
            </Typography>
            <Typography
              sx={{
                fontSize: '14px',
              }}
            >
              <Link
                href={`/schedule/event/${props.eventRn}`}
              >
                {event.info.title}
              </Link>
            </Typography>
          </Stack>
          <Stack>
            <Typography
              sx={{
                fontWeight: 600,
              }}
            >
              <Translate expression='client.ClientDetail.Scheduling.ScheduleDetail.EventInfo.Description'/>
            </Typography>
            <Box
              sx={{
                fontSize: '14px',
              }}
            >
              {
                !event.info.description.blob &&
                translate('client.ClientDetail.Scheduling.ScheduleDetail.EventInfo.NoDescription')
              }
              {
                event.info.description.blob &&
                <MarkdownViewer
                  value={event.info.description.blob}
                  truncate={true}
                />
              }
            </Box>
          </Stack>
          <Stack>
            <Grid container>
              <Grid item xs={6}>
                <Stack>
                  <Typography
                    sx={{
                      fontWeight: 600,
                    }}
                  >
                    <Translate expression='client.ClientDetail.Scheduling.ScheduleDetail.EventDate.FixedEvent.StartTime'/>
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '14px',
                    }}
                  >
                    <ShortDateText dateTimestamp={startTimeMoment}/>
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={6}>
                <Stack>
                  <Typography
                    sx={{
                      fontWeight: 600,
                    }}
                  >
                    <Translate expression='client.ClientDetail.Scheduling.ScheduleDetail.EventDate.FixedEvent.EndTime'/>
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '14px',
                    }}
                  >
                    <ShortDateText dateTimestamp={endTimeMoment}/>
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={12}>
                <Stack>
                  <Typography
                    sx={{
                      fontWeight: 600,
                    }}
                  >
                    <Translate expression='client.ClientDetail.Scheduling.ScheduleDetail.EventDate.FixedEvent.Timezone'/>
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '14px',
                    }}
                  >
                    {event.eventData.timezone}
                  </Typography>
                </Stack>
              </Grid>
            </Grid>
          </Stack>
          {
            event.breaks && (
              <Stack>
                <Typography
                  sx={{
                    fontWeight: 600,
                  }}
                >
                  <Translate expression='client.ClientDetail.Scheduling.ScheduleDetail.Breaks.Title'/>
                </Typography>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Typography
                      sx={{
                        fontSize: '14px',
                        fontWeight: 500,
                      }}
                    >
                      <Translate expression='client.ClientDetail.Scheduling.ScheduleDetail.Breaks.BreakDuration'/>:
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '14px',
                      }}
                    >
                      <Translate
                        expression="schedule.eventDetail.TimeSection.Minutes"
                        substitute={{breakDuration: event.breaks.breakDuration}}
                      />
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography
                      sx={{
                        fontSize: '14px',
                        fontWeight: 500,
                      }}
                    >
                      <Translate expression='client.ClientDetail.Scheduling.ScheduleDetail.Breaks.BreakCount'/>:
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '14px',
                      }}
                    >
                      {event.breaks.breakCount}
                    </Typography>
                  </Grid>
                </Grid>
              </Stack>
            )
          }
          <Stack>
            <Typography
              sx={{
                fontWeight: 600,
              }}
            >
              <Translate expression='client.ClientDetail.Scheduling.ScheduleDetail.Caregiver.CaregiverSearch'/>
            </Typography>
            <Typography
              sx={{
                fontSize: '14px',
              }}
            >
              {
                caregiverData && (
                  <Link href={`/caregiver/${caregiverData.caregiverRn}`}>
                    {caregiverData.caregiverDetail.caregiverName}
                  </Link>
                )
              }
              {
                !caregiverData && (
                  <Skeleton sx={{fontSize: '14px', width: '100px'}} />
                )
              }
            </Typography>
          </Stack>
          {
            (event.recurrenceRn) &&
            <Stack
              direction='row'
              spacing={1}
              sx={{
                backgroundColor: 'grey.200',
                borderRadius: '8px',
                color: 'grey.700',
                p: 1,
              }}
            >
              <Box sx={{py: 2}}>
                <Cached/>
              </Box>
              <Stack >
                <Translate
                  expression='client.ClientDetail.Scheduling.ScheduleDetail.Recurrence.RotationRecurrenceMessage'
                />
                <Button
                  href={'#'}
                  variant='text'
                  sx={{textTransform: 'none', justifyContent: 'flex-start', m: 0, p: 0}}
                  onClick={(e) => e.preventDefault() & openRecurrence(event.recurrenceRn, event)}
                >
                  <Translate expression='client.ClientDetail.Scheduling.ScheduleDetail.Recurrence.EditRecurrence'/>
                </Button>
              </Stack>
            </Stack>
          }
          {
            (event.liveInRn) &&
            <Stack
              direction='row'
              spacing={1}
              sx={{
                backgroundColor: 'grey.200',
                borderRadius: '8px',
                color: 'grey.700',
                p: 1,
              }}
            >
              <Box sx={{py: 2}}>
                <Cached/>
              </Box>
              <Stack >
                <Translate
                  expression='client.ClientDetail.Scheduling.ScheduleDetail.LiveIn.RotationLiveInMessage'
                />
                <Button
                  href={'#'}
                  variant='text'
                  sx={{textTransform: 'none', justifyContent: 'flex-start', m: 0, p: 0}}
                  onClick={(e) => e.preventDefault() & openLiveIn(event.liveInRn, event)}
                >
                  <Translate expression='client.ClientDetail.Scheduling.ScheduleDetail.LiveIn.EditLiveIn'/>
                </Button>
              </Stack>
            </Stack>
          }
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant='outlined' onClick={onClose}>
          <Translate expression='generic.Close' />
        </Button>
        {
          !statusStyles.isInactive &&
          <Button color="error" variant="contained" onClick={() => deleteEvent(eventRn, event)}>
            <Translate expression="client.ClientDetail.Scheduling.DeleteEvent" />
          </Button>
        }
      </DialogActions>
    </Dialog>
  );
};

ViewEvent.propTypes = {
  onClose: PropTypes.func.isRequired,
  eventRn: PropTypes.string.isRequired,
  event: PropTypes.object.isRequired,
  openRecurrence: PropTypes.func.isRequired,
  openLiveIn: PropTypes.func.isRequired,
  deleteEvent: PropTypes.func.isRequired,
};

export default React.memo(ViewEvent);
